/**
 * aai-motion.js — reveal-on-scroll and count-up.
 *
 * Progressive enhancement only. Without this module every element is visible
 * and every number is already rendered. Respects prefers-reduced-motion by
 * doing nothing except marking elements visible.
 */
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

function markReady() {
  if (!reduced.matches) document.documentElement.setAttribute('data-aai-motion', 'ready');
}

function revealAll(root = document) {
  root.querySelectorAll('.aai-reveal').forEach((el) => el.classList.add('is-visible'));
}

function initReveal(root = document) {
  const targets = root.querySelectorAll('.aai-reveal:not(.is-visible)');
  if (!targets.length) return;
  if (reduced.matches || !('IntersectionObserver' in window)) return revealAll(root);

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
  );
  targets.forEach((el) => io.observe(el));
}

/**
 * Count-up: <span data-aai-count="1250" data-aai-count-prefix="" data-aai-count-suffix="+">1,250+</span>
 * The rendered text is the final value, so no-JS users see the number.
 */
function animateCount(el) {
  const end = parseFloat(el.dataset.aaiCount);
  if (Number.isNaN(end)) return;
  const prefix = el.dataset.aaiCountPrefix ?? '';
  const suffix = el.dataset.aaiCountSuffix ?? '';
  const decimals = (el.dataset.aaiCount.split('.')[1] || '').length;
  const duration = 1100;
  const start = performance.now();
  const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  el.setAttribute('aria-label', el.textContent.trim());
  const step = (now) => {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    el.textContent = prefix + fmt.format(end * eased) + suffix;
    if (t < 1) requestAnimationFrame(step);
    else el.removeAttribute('aria-label');
  };
  requestAnimationFrame(step);
}

function initCount(root = document) {
  const targets = root.querySelectorAll('[data-aai-count]');
  if (!targets.length || reduced.matches || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.6 }
  );
  targets.forEach((el) => io.observe(el));
}

function init(root = document) {
  markReady();
  initReveal(root);
  initCount(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Theme editor: re-run when a section is (re)loaded.
document.addEventListener('shopify:section:load', (e) => init(e.target));

reduced.addEventListener('change', () => {
  if (reduced.matches) {
    document.documentElement.removeAttribute('data-aai-motion');
    revealAll();
  }
});

export { init, initReveal, initCount };
